import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Globe, Clock, CheckCircle2, Loader2, Send, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const areasOfInterest = [
  'Strategy & Operational Efficiency',
  'Finance Advisory',
  'Operations',
  'Technology Consulting',
  'GI/AI/LLM Governance',
  'Grants/Contracts',
  'Data Readiness',
  'Other',
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '', organization: '', email: '', phone: '',
    area_of_interest: '', message: '', consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="contact-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
            <CheckCircle2 className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Thank You!</h1>
          <p className="text-lg text-muted-foreground mb-8">
            We&apos;ve received your message and will respond within 1 business day.
          </p>
          <a href="/" className="inline-flex items-center px-8 py-3 rounded-lg font-medium bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
            Return Home
            <ArrowRight className="ml-2" size={18} />
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-top" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Let&apos;s <span className="text-gradient">Talk</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Ready to improve operational performance, strengthen financial management, or establish AI governance? We respond within 1 business day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-6">Contact Information</h3>
                <ul className="space-y-4">
                  {[
                    { icon: Mail, label: 'Email', value: 'info@anjolc.com', href: 'mailto:info@anjolc.com', color: 'cyan' },
                    { icon: Globe, label: 'Website', value: 'www.anjolc.com', href: 'https://www.anjolc.com', color: 'purple' },
                    { icon: Clock, label: 'Response', value: 'Within 1 business day', color: 'emerald' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center space-x-4 group">
                      <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/10 flex items-center justify-center group-hover:bg-${item.color}-500/20 transition-colors`}>
                        <item.icon className={`text-${item.color}-400`} size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-foreground hover:text-cyan-400 transition-colors">{item.value}</a>
                        ) : (
                          <p className="text-foreground">{item.value}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl glass">
                <h4 className="font-semibold text-foreground mb-3">Procurement-Friendly</h4>
                <p className="text-sm text-muted-foreground">
                  Request a Capability Statement or SOQ tailored for your organization.
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              <div className="p-8 rounded-2xl glass glow-border">
                <h3 className="text-2xl font-bold text-foreground mb-6">Send us a message</h3>
                {error && <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400" data-testid="contact-error">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { name: 'name', label: 'Name', required: true, type: 'text', placeholder: 'Your full name' },
                      { name: 'organization', label: 'Organization', required: true, type: 'text', placeholder: 'Your organization' },
                      { name: 'email', label: 'Email', required: true, type: 'email', placeholder: 'you@company.com' },
                      { name: 'phone', label: 'Phone', required: false, type: 'tel', placeholder: '+1 (555) 123-4567' },
                    ].map((field) => (
                      <div key={field.name}>
                        <label htmlFor={field.name} className="block text-sm font-medium text-foreground mb-2">
                          {field.label} {field.required && <span className="text-cyan-400">*</span>}
                        </label>
                        <input
                          type={field.type} id={field.name} name={field.name} required={field.required}
                          value={formData[field.name]} onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-foreground placeholder:text-muted-foreground transition-all"
                          placeholder={field.placeholder} data-testid={`input-${field.name}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label htmlFor="area_of_interest" className="block text-sm font-medium text-foreground mb-2">
                      Area of Interest <span className="text-cyan-400">*</span>
                    </label>
                    <select
                      id="area_of_interest" name="area_of_interest" required
                      value={formData.area_of_interest} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-foreground transition-all"
                      data-testid="select-interest"
                    >
                      <option value="">Select an area...</option>
                      {areasOfInterest.map((area) => <option key={area} value={area}>{area}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                      Message <span className="text-cyan-400">*</span>
                    </label>
                    <textarea
                      id="message" name="message" required rows={5}
                      value={formData.message} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-foreground placeholder:text-muted-foreground resize-none transition-all"
                      placeholder="Tell us about your project or challenge..." data-testid="input-message"
                    />
                  </div>
                  <div className="flex items-start space-x-3 relative z-10">
                    <input
                      type="checkbox" id="consent" name="consent" required
                      checked={formData.consent} onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded bg-secondary border-border text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                      data-testid="checkbox-consent"
                    />
                    <label htmlFor="consent" className="text-sm text-muted-foreground cursor-pointer">
                      I understand ANJO Consultants will use this information to respond to my inquiry. <span className="text-cyan-400">*</span>
                    </label>
                  </div>
                  <button
                    type="submit" disabled={isSubmitting}
                    className="relative z-10 group inline-flex items-center px-10 py-4 rounded-lg font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/25"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="animate-spin mr-2" size={20} />Sending...</>
                    ) : (
                      <><Send className="mr-2" size={18} />Send Message</>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
